import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

interface BuildInfo {
  configuration: string;
  buildTime: string;
  environmentFile: string;
  keyIdentifiers: {
    production: boolean;
    iamhost: string;
    gatewayUrl: string;
    keycloakUrl: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class BuildInfoService {
  
  constructor() { }

  /**
   * Get comprehensive build and environment information
   * @returns BuildInfo object with environment details
   */
  getBuildInfo(): BuildInfo {
    const buildInfo: BuildInfo = {
      configuration: this.detectConfiguration(),
      buildTime: new Date().toISOString(),
      environmentFile: this.getEnvironmentFileName(),
      keyIdentifiers: {
        production: environment.production,
        iamhost: environment.iamhost,
        gatewayUrl: environment.gatewayUrl,
        keycloakUrl: environment.keycloak.url
      }
    };

    return buildInfo;
  }

  /**
   * Detect which configuration is being used based on environment values
   * @returns Configuration name
   */
  private detectConfiguration(): string {
    if (environment.production === true && environment.iamhost.includes('assaying.qualix.ai')) {
      return 'production';
    } else if (environment.iamhost.includes('assaying-qa.qualix.ai')) {
      return 'qa';
    } else if (environment.iamhost.includes('assaying-dev.qualix.ai')) {
      return 'dev';
    } else if (environment.iamhost.includes('localhost')) {
      return 'local';
    } else {
      return 'unknown';
    }
  }

  /**
   * Get the environment file name based on detected configuration
   * @returns Environment file name
   */
  private getEnvironmentFileName(): string {
    const config = this.detectConfiguration();
    switch (config) {
      case 'production':
        return 'environment.prod.ts';
      case 'qa':
        return 'environment.qa.ts';
      case 'dev':
        return 'environment.dev.ts';
      case 'local':
        return 'environment.ts';
      default:
        return 'unknown';
    }
  }

  /**
   * Log comprehensive build information to console
   */
  logBuildInfo(): void {
    const buildInfo = this.getBuildInfo();
    
    console.group('🏗️ BUILD & ENVIRONMENT INFORMATION');
    console.log('📄 Configuration:', buildInfo.configuration);
    console.log('📁 Environment File:', buildInfo.environmentFile);
    console.log('⏰ Build Time:', buildInfo.buildTime);
    console.log('🔧 Production Mode:', buildInfo.keyIdentifiers.production);
    console.log('🌐 IAM Host:', buildInfo.keyIdentifiers.iamhost);
    console.log('🔗 Gateway URL:', buildInfo.keyIdentifiers.gatewayUrl);
    console.log('🔐 Keycloak URL:', buildInfo.keyIdentifiers.keycloakUrl);
    console.groupEnd();

    // Add to window object for easy access in browser console
    (window as any).buildInfo = buildInfo;
    console.log('💡 TIP: Access build info anytime with window.buildInfo');
  }

  /**
   * Get environment-specific styling for UI display
   * @returns CSS class and color for environment badge
   */
  getEnvironmentBadge(): { class: string; color: string; text: string } {
    const config = this.detectConfiguration();
    switch (config) {
      case 'production':
        return { class: 'badge-danger', color: '#dc3545', text: 'PRODUCTION' };
      case 'qa':
        return { class: 'badge-warning', color: '#ffc107', text: 'QA' };
      case 'dev':
        return { class: 'badge-info', color: '#17a2b8', text: 'DEVELOPMENT' };
      case 'local':
        return { class: 'badge-success', color: '#28a745', text: 'LOCAL' };
      default:
        return { class: 'badge-secondary', color: '#6c757d', text: 'UNKNOWN' };
    }
  }
} 